import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { X, ArrowRight, GitCompare } from "lucide-react";

/**
 * CompareTray, sticky bottom drawer showing currently-selected items to compare.
 * Renders nothing when compare list is empty.
 */
export default function CompareTray() {
  const { compare, removeCompare, clearCompare } = useApp();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (compare.length === 0) { setItems([]); return; }
    (async () => {
      const docs = await Promise.all(
        compare.map((id) => api.get(`/hardware/${id}`).then((r) => r.data).catch(() => null))
      );
      setItems(docs.filter(Boolean));
    })();
  }, [compare]);

  if (compare.length === 0) return null;

  return (
    <div
      data-testid="compare-tray"
      className="fixed bottom-0 left-0 right-0 z-40 bg-[#0F1816]/95 backdrop-blur-xl border-t-2 border-[#D97742] shadow-[0_-8px_30px_rgba(0,0,0,0.6)]"
    >
      <div className="container-x py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <GitCompare className="w-4 h-4 text-[#D97742]" />
          <span className="text-xs uppercase tracking-wider font-semibold text-white">Vergelijk</span>
          <span className="text-xs text-[#A8B3AD]">({compare.length}/3)</span>
        </div>
        <div className="flex gap-2 flex-1 overflow-x-auto">
          {items.map((h) => (
            <div
              key={h.id}
              className="flex items-center gap-2 px-2.5 py-1.5 bg-[#1F3A2E]/40 border border-[#1F3A2E] rounded-md shrink-0"
              data-testid={`compare-tray-${h.sku}`}
            >
              {h.image_url && (
                <img src={h.image_url} alt={h.name} className="w-8 h-8 object-contain rounded bg-[#0F1816]" />
              )}
              <div className="min-w-0">
                <div className="text-xs text-white truncate max-w-[160px]">{h.name}</div>
                <div className="text-[10px] font-mono text-[#A8B3AD]">{h.sku}</div>
              </div>
              <button
                onClick={() => removeCompare(h.id)}
                className="text-[#A8B3AD] hover:text-white p-1"
                aria-label="Verwijder"
                data-testid={`compare-remove-${h.sku}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={clearCompare}
            className="text-xs text-[#A8B3AD] hover:text-white px-2 py-1"
            data-testid="compare-clear"
          >
            Reset
          </button>
          <Link
            to={`/compare?items=${compare.join(",")}`}
            data-testid="compare-go"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition ${compare.length >= 2 ? "bg-[#D97742] text-white hover:bg-[#c46939]" : "bg-[#1F3A2E] text-[#5d6a64] cursor-not-allowed pointer-events-none"}`}
          >
            Vergelijk <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
